import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import TemplateListHeader from "../components/TemplateListHeader";
import TemplateCard from "../components/TemplateCard";
import TemplateTableRow from "../components/TemplateTableRow";
import TemplatePreviewModal from "../components/TemplatePreviewModal";
import { UsageLimitWarning } from "../components/UsageLimitWarning";
import Pagination from "../components/Pagination";
import useSubscriptionLimits from "../hooks/useSubscriptionLimits";

const LANGUAGE_MAP = {
  en_US: "English (US)", es: "Spanish", fr: "French", de: "German",
  it: "Italian", pt: "Portuguese", ru: "Russian", zh: "Chinese",
  ja: "Japanese", ko: "Korean", hi: "Hindi", ar: "Arabic",
};

const TemplateList = () => {
  const navigate = useNavigate();
  const [templates, setTemplates]           = useState([]);
  const [loading, setLoading]               = useState(true);
  const [currentPage, setCurrentPage]       = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const templatesPerPage = 10;
  const { usage, subscription } = useSubscriptionLimits();

  useEffect(() => {
    api.get("/getTemplate")
      .then((r) => setTemplates(r.data.templates || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const indexOfLast    = currentPage * templatesPerPage;
  const indexOfFirst   = indexOfLast - templatesPerPage;
  const currentTemplates = templates.slice(indexOfFirst, indexOfLast);
  const totalPages     = Math.ceil(templates.length / templatesPerPage);

  if (loading) {
    return <LoadingSpinner fullScreen size="lg" text="Loading templates..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TemplateListHeader />

      {usage && subscription && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
          <UsageLimitWarning
            feature="Messages (Current Plan)"
            current={usage?.messagesThisPlan || 0}
            limit={subscription.subscriptionPlan?.maxMessages || -1}
            onUpgrade={() => navigate("/subscription-plans")}
          />
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {templates.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
            <p className="text-gray-500 mb-6">Get started by creating your first WhatsApp template.</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden">
              <div className="grid gap-4">
                {currentTemplates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    languageMap={LANGUAGE_MAP}
                    onCardClick={setSelectedTemplate}
                  />
                ))}
              </div>
              {templates.length > templatesPerPage && (
                <div className="mt-6">
                  <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                </div>
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block">
              <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {["Template Name", "Category", "Language", "Header Type", "Status", "Created At"].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentTemplates.map((template) => (
                      <TemplateTableRow
                        key={template.id}
                        template={template}
                        languageMap={LANGUAGE_MAP}
                        onRowClick={setSelectedTemplate}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              {templates.length > templatesPerPage && (
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
              )}
            </div>
          </>
        )}
      </div>

      {selectedTemplate && (
        <TemplatePreviewModal
          isOpen={true}
          onClose={() => setSelectedTemplate(null)}
          template={selectedTemplate}
        />
      )}
    </div>
  );
};

export default TemplateList;
